import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../api/client';
import type { AuditLogPageDto } from '../api/generated/client';
import { Box, Stack, Typography } from '../components/mui-system';

const statuses: Record<string, string> = {
  completed: 'Завершено',
  failed: 'Ошибка / отказ',
  started: 'Исход пока неизвестен',
};

export function AuditLogPanel() {
  const [search] = useSearchParams();
  const [actorId, setActorId] = useState('');
  const [workspaceId, setWorkspaceId] = useState(search.get('workspaceId') ?? '');
  const [status, setStatus] = useState('');
  const [filters, setFilters] = useState({ actorId: '', workspaceId, status: '' });
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [refresh, setRefresh] = useState(0);
  const [page, setPage] = useState<AuditLogPageDto | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const cursor = cursors[cursors.length - 1];

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setPage(null);
    api
      .auditLog({ ...filters, cursor, limit: 50 })
      .then(
        (result) => {
          if (active) setPage(result);
        },
        (caught) => {
          if (active)
            setError(caught instanceof Error ? caught.message : 'Не удалось загрузить журнал');
        },
      )
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters, cursor, refresh]);

  function apply(event: FormEvent) {
    event.preventDefault();
    setCursors([undefined]);
    setFilters({ actorId: actorId.trim(), workspaceId: workspaceId.trim(), status });
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h3" mb={1}>
          Журнал действий
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Изменения, входы, выходы и отказы в доступе. Доступен только суперадминам.
          {page && ` Срок хранения — ${page.retentionDays} дней.`}
        </Typography>
        <Stack
          component="form"
          onSubmit={apply}
          direction={{ xs: 'column', md: 'row' }}
          gap={2}
          mb={2}
        >
          <TextField
            label="ID пользователя"
            value={actorId}
            onChange={(event) => setActorId(event.target.value)}
            size="small"
          />
          <TextField
            label="ID workspace"
            value={workspaceId}
            onChange={(event) => setWorkspaceId(event.target.value)}
            size="small"
          />
          <TextField
            select
            label="Результат"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Все результаты</MenuItem>
            {Object.entries(statuses).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Button type="submit" disabled={loading}>
            Применить
          </Button>
          <Button
            disabled={loading}
            onClick={() => {
              setCursors([undefined]);
              setRefresh((value) => value + 1);
            }}
          >
            Обновить
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <CircularProgress aria-label="Загрузка журнала" size={24} />}
        {page && (
          <>
            <TableContainer>
              <Table size="small" aria-label="Журнал действий пользователей">
                <TableHead>
                  <TableRow>
                    <TableCell>Время</TableCell>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell>Результат</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {page.items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(row.createdAt).toLocaleString('ru')}
                        {row.completedAt && (
                          <Typography variant="caption" display="block">
                            До {new Date(row.completedAt).toLocaleTimeString('ru')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.actorName}
                        <Button
                          size="small"
                          title="Фильтровать по пользователю"
                          onClick={() => {
                            setActorId(row.actorId);
                            setFilters({ ...filters, actorId: row.actorId });
                            setCursors([undefined]);
                          }}
                          sx={{ display: 'block', fontSize: 11, textTransform: 'none' }}
                        >
                          {row.actorId}
                        </Button>
                        {row.apiTokenId && (
                          <Typography variant="caption">API-токен: {row.apiTokenId}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        {row.workspaceId ? (
                          <Button
                            size="small"
                            title="Фильтровать по workspace"
                            onClick={() => {
                              setWorkspaceId(row.workspaceId!);
                              setFilters({ ...filters, workspaceId: row.workspaceId! });
                              setCursors([undefined]);
                            }}
                            sx={{ fontSize: 11, textTransform: 'none' }}
                          >
                            {row.workspaceId}
                          </Button>
                        ) : (
                          'Общее действие'
                        )}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere', minWidth: 220 }}>
                        {row.action}
                      </TableCell>
                      <TableCell>
                        {statuses[row.status] ?? `Неизвестный статус: ${row.status}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!page.items.length && (
              <Typography py={2}>Записей за срок хранения с этими фильтрами нет.</Typography>
            )}
            <Stack direction="row" alignItems="center" gap={2} mt={2}>
              <Button
                disabled={cursors.length === 1}
                onClick={() => setCursors((values) => values.slice(0, -1))}
              >
                Назад
              </Button>
              <Typography>Страница {cursors.length}</Typography>
              <Button
                disabled={!page.nextCursor}
                onClick={() => setCursors((values) => [...values, page.nextCursor!])}
              >
                Далее
              </Button>
            </Stack>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                «Завершено» означает завершение запроса; результат фоновой задачи смотрите в её
                разделе. При ошибке возможны частичные изменения. Неизвестный исход означает, что
                завершение не зафиксировано.
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
